import { Link } from "@remix-run/react";
import { isCourseFree } from "@repo/shared";
import { useTranslation } from "react-i18next";

import DefaultPhotoCourse from "~/assets/svgs/default-photo-course.svg";
import { CoursePriceDisplay } from "~/components/CoursePriceDisplay/CoursePriceDisplay";
import { ResilientImage } from "~/components/ResilientImage";
import { cn } from "~/lib/utils";
import { CourseCardActions } from "~/modules/Cart/CourseCardActions";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";
import { resolveCourseDetailHref } from "~/utils/courseLinks";
import { imageVariant } from "~/utils/imageVariant";

import type { GetAvailableCoursesResponse } from "~/api/generated-api";
type CourseWithLocales = GetAvailableCoursesResponse["data"][number] & {
  availableLocales?: string[];
  baseLanguage?: string;
};

export type PublicCourseCardProps = {
  course: CourseWithLocales;
  isEnrolled: boolean;
  isPurchased: boolean;
  isPreinscribed?: boolean;
  isLoggedIn: boolean;
  /**
   * Quedó sin uso en `/courses` cuando se retiró el filtro de idioma (no hay un solo curso
   * publicado en inglés en ningún ambiente). Se mantiene opcional para no romper la firma pública.
   */
  displayLanguage?: string;
  priority?: boolean;
};

/**
 * Tarjeta de curso del catálogo público. El otro importador es `IAPracticaCoursesSection`, que hoy
 * NO está montada (`Home.page.tsx:10` la tiene comentada) pero comparte la firma: si vuelve a la
 * home, hereda este layout.
 *
 * Bajó de 524px a ~372px en mobile SIN tocar el thumbnail. Los 152px que se recortaron eran cromo,
 * no producto:
 * - la franja de metadata con borde propio (41px) se disolvió en una línea dentro del body;
 * - el footer "Ver detalles →" (37px) se eliminó: era el TERCER link al mismo href de la tarjeta,
 *   una parada de teclado extra que no llevaba a ningún lado nuevo;
 * - la descripción pasa a `hidden sm:block`: en una tarjeta de una columna, dos líneas de resumen
 *   compiten con el título por el mismo trabajo.
 *
 * El thumbnail NO se toca — y ahora es verdad. `aspect-video`, ancho completo, sin crop y SIN NADA
 * encima: las dos portadas publicadas tienen el nombre del curso y su subtítulo dentro del JPG, así
 * que cualquier cosa apoyada arriba tapa tipografía. La versión anterior de este archivo escribía
 * esta misma promesa y tres líneas más abajo metía el badge de estado en `absolute right-2 top-2`,
 * justo sobre el ángulo donde vive el título compuesto. El badge bajó a la fila de metadata.
 *
 * Rojos por tarjeta: de 4 a 2. §2.3 del manual quiere UNO (el rojo es el color de la acción "y en
 * ningún otro lugar") y no llegamos: quedan el CTA sólido y el chip de precio, que `CoursePriceDisplay`
 * pinta `bg-landing-red-50 border-landing-red-200 text-landing-red` para el caso "Gratis" y con el
 * precio en `text-landing-red` para el caso con precio. Ese componente lo comparten la tarjeta y el
 * sidebar del detalle, así que sacarle el rojo es una tarea aparte — no se toca desde acá. Lo que sí
 * se fue: el badge de estado (era el tercer rojo) y la categoría y el "Ver detalles" que se fueron
 * con los bloques que los contenían.
 *
 * DEUDA DE CONTENIDO, no de código: el título aparece dos veces en la tarjeta — impreso dentro del
 * JPG de la portada y otra vez como `<h3>`. El `<h3>` no se puede sacar (es el nombre accesible, lo
 * que indexa un crawler, y lo único que queda cuando la portada es el placeholder), así que la
 * salida es regenerar las portadas SIN tipografía adentro. Es trabajo de brand assets.
 */
/**
 * A dónde lleva la tarjeta. Es la única regla de destino de esta pantalla y vive acá, aparte, para
 * poder testearla sin montar la tarjeta entera.
 *
 * La tarjeta se usa en los dos catálogos —el público (`/courses`) y el autenticado (`/library`)— y
 * hasta ahora mandaba a todo el mundo a la pública: un alumno logueado que tocaba un curso de su
 * biblioteca quedaba expulsado del producto.
 *
 * La decisión de destino vive en `resolveCourseDetailHref` (utils/courseLinks), compartida con las
 * secciones de la home, para que el mismo curso no lleve a pantallas distintas según desde qué
 * bloque se lo toque. Se mantiene el nombre viejo como alias porque hay tests que lo fijan.
 */
export const resolveCourseCardHref = resolveCourseDetailHref;

export function PublicCourseCard({
  course,
  isEnrolled,
  isPurchased,
  isPreinscribed = false,
  isLoggedIn,
  displayLanguage,
  priority = false,
}: PublicCourseCardProps) {
  const { t } = useTranslation();
  const { language: webLanguage } = useLanguageStore();

  const courseLink = resolveCourseCardHref({
    slug: course.slug,
    isLoggedIn,
    displayLanguage,
    webLanguage,
  });

  const {
    author,
    category,
    courseLessonCount,
    description,
    duration,
    hasFreeChapters,
    priceInCents,
    mercadopagoPriceInCents,
    mercadopagoProductId,
    slug,
    thumbnailUrl,
    title,
  } = course;

  const isFreeCourse = isCourseFree({ priceInCents, mercadopagoProductId });

  // UNA sola fuente de duración, nunca las dos: `duration` es un string libre cargado desde el
  // admin y hoy vale "8 horas" y "16 clases", así que sumarle el conteo de clases daría
  // "16 clases · 16 clases". El conteo de capítulos se fue de la tarjeta: ya está impreso dentro
  // del thumbnail de los dos cursos publicados.
  const durationText =
    duration?.trim() ||
    (courseLessonCount ? t("landing.courses.card.lessons", { count: courseLessonCount }) : null);
  const previewText =
    hasFreeChapters && !isFreeCourse ? t("landing.courses.card.freeLessons") : null;
  const metaText = [durationText, previewText].filter(Boolean).join(" · ");

  // El estado "curso gratis" YA NO se rotula acá: lo dice el chip de precio del footer, y decirlo
  // tres veces por tarjeta (badge "CURSO GRATIS" + chip "Gratis" + CTA "Inscribirme Gratis") es
  // exactamente el cromo que esta pantalla vino a sacar. Queda sólo lo que el resto de la tarjeta no
  // puede decir: que este usuario ya lo compró o ya está inscripto.
  //
  // Un curso GRATIS nunca dice "Comprado": al inscribirse, la API le marca `purchasedAt` (es lo
  // que habilita las clases, ver enrollCourse), así que `isPurchased` llega en true también para
  // los gratis y la biblioteca le ponía "COMPRADO" a un curso que nadie pagó.
  const showsPurchased = isPurchased && !isFreeCourse;
  const statusLabel = showsPurchased
    ? t("landing.courses.card.purchased")
    : isEnrolled || isPurchased
      ? t("landing.courseDetail.enrolled")
      : isPreinscribed
        ? "Preinscrito"
        : null;

  // Nombre accesible de la tarjeta. Sin esto los `<article>` aparecen en el árbol de accesibilidad
  // como `[article] ""`: un lector de pantalla que lista las regiones de la página anuncia N
  // artículos sin nombre y hay que entrar en cada uno para saber de qué curso es. El `<h3>` ya tiene
  // el texto correcto, así que se apunta a él con `aria-labelledby` en vez de duplicar el título en
  // un `aria-label` (que se desincronizaría). El id se arma con el slug, que es único por curso y
  // estable entre servidor y cliente — con `useId()` no lo sería.
  const titleId = `course-card-title-${slug}`;

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        "group flex h-full w-full flex-col overflow-hidden rounded-xl border border-landing-border bg-landing-white transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-landing-red hover:shadow-landing-md motion-reduce:transform-none",
      )}
    >
      {/* Link redundante al mismo href del título: clickeable con el mouse, pero FUERA del orden de
          tabulación. Con esto las paradas de teclado por tarjeta bajan de 4 a 2. */}
      <Link to={courseLink} tabIndex={-1} aria-hidden="true" className="block">
        <div className="relative aspect-video overflow-hidden bg-landing-stone-100">
          <ResilientImage
            // WebP de 960 px por el proxy en vez de la portada original (cientos de KB).
            src={imageVariant(thumbnailUrl, 960)}
            fallbackSrc={DefaultPhotoCourse}
            alt={title}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            width={640}
            height={360}
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
          />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        {/* Fila de metadata. El badge de estado vive ACÁ y no sobre el thumbnail por dos razones, y
            las dos son bugs que había: (1) el `<Link>` del thumbnail es `aria-hidden` — es el link
            redundante, fuera del orden de tabulación — así que todo lo que cuelgue de él es invisible
            para un lector de pantalla, y un usuario ciego no podía saber que ya había comprado el
            curso; (2) tapaba el título compuesto dentro del JPG de la portada.
            §10.6 del manual: `radius-sm` (6px = `rounded-md`), 11px 600 uppercase. El estado
            comprado usa una combinación neutra de la marca; inscripto usa success. La tarjeta no
            suma un borde lateral de estado: ese acento azul venía del template y competía con la
            portada y con el CTA principal.

            ═══ La caja alta del badge se DEJA como está, a propósito ═══
            `landing-eyebrow` aplica `text-transform: uppercase`, y el DOM ya dice el texto en caja
            normal ("Comprado" / "Inscripto") — la caja alta la pone sólo el CSS, que es lo correcto.
            Lo que se verificó es que Chrome computa el nombre accesible a partir del texto
            RENDERIZADO, así que en el árbol de accesibilidad figura "COMPRADO". NO se le pone un
            `aria-label` para forzar la caja normal, por dos razones: (1) el badge es un `<span>` sin
            rol, o sea rol `generic`, y ARIA prohíbe nombrar elementos genéricos — un `aria-label` acá
            no está garantizado que se exponga y en varios navegadores se ignora, así que sería un
            arreglo de mentira; (2) el riesgo real es bajo: NVDA y JAWS deletrean mayúsculas sólo con
            cadenas cortas tipo sigla, y "COMPRADO"/"INSCRIPTO" son palabras de 8 y 9 letras que
            pronuncian como palabra. Si algún día hay que forzarlo, la salida honesta es sacarle el
            `uppercase` a esta instancia, no mentirle al árbol. */}
        {(statusLabel || metaText !== "") && (
          <div className="flex min-w-0 items-center gap-2">
            {statusLabel && (
              <span
                className={cn(
                  "landing-eyebrow shrink-0 rounded-md px-2 py-1",
                  showsPurchased
                    ? "bg-landing-stone-100 text-landing-stone-700"
                    : isPreinscribed
                      ? "bg-landing-warning-light text-landing-warning"
                      : "bg-landing-success-light text-landing-success",
                )}
              >
                {statusLabel}
              </span>
            )}
            {metaText !== "" && (
              <span className="landing-meta truncate text-landing-muted">{metaText}</span>
            )}
          </div>
        )}
        {/* `min-h` fijo de dos líneas: sin esto, un título de una línea y otro de dos desalinean los
            footers de la misma fila de la grilla. Dos líneas de 18px con `leading-[1.25]` miden 45px,
            así que el valor tiene que ser el múltiplo de 4 de ARRIBA: con `min-h-11` (44) el caso de
            dos líneas se pasa 1px y la alineación se rompe igual. `min-h-12` = 48. */}
        <h3 id={titleId} className={cn("min-h-12", (statusLabel || metaText !== "") && "mt-2")}>
          <Link
            to={courseLink}
            className="font-display line-clamp-2 text-[18px] font-semibold leading-[1.25] text-landing-text hover:text-landing-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red focus-visible:ring-offset-2 focus-visible:ring-offset-landing-white"
          >
            {title}
          </Link>
        </h3>
        {/* El toggle responsive va en un WRAPPER, no en el mismo elemento que el `line-clamp-2`:
            `line-clamp-*` de Tailwind necesita `display:-webkit-box`, y un `sm:block` en la misma
            clase lo pisa a partir de 640px porque las variantes responsive se emiten después. Con
            las dos clases juntas la descripción se desclampeaba en desktop y la tarjeta se iba de
            374px a 650px (medido). */}
        {/* `div` y NO `p`: `description` viene de la base como rich text y ARRANCA con `<p>`.
            Un `<p>` adentro de otro `<p>` es HTML inválido: el parser del browser CIERRA el
            de afuera al toparse con el de adentro, y el DOM resultante deja de coincidir con
            el árbol de React. Con la página en SPA daba igual (nunca se hidrataba markup del
            servidor); desde que /courses se renderiza en el servidor, esto rompía la
            hidratación en TODOS los renders — React tiraba "Hydration failed" y regeneraba el
            árbol entero en el cliente. Medido: 6/6 fallas con tarjetas, 0/4 sin tarjetas.
            Visualmente no cambia nada: preflight de Tailwind deja `<p>` sin márgenes y
            `line-clamp-*` funciona en cualquier bloque. */}
        <div className="mt-2 hidden sm:block">
          <div className="line-clamp-2 text-[14px] leading-snug text-landing-muted">
            <span dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        </div>
      </div>

      {/* Footer de 69px: una sola fila de 44 con el precio como chip y la acción al lado. Antes el
          precio y los botones eran dos bloques apilados dentro del body (~110px). */}
      <div className="flex items-center gap-2 border-t border-landing-border px-4 py-3">
        <CoursePriceDisplay
          priceInCents={priceInCents}
          mercadopagoPriceInCents={mercadopagoPriceInCents}
          mercadopagoProductId={mercadopagoProductId}
          className="shrink-0"
        />

        <CourseCardActions
          course={{
            id: course.id ?? "",
            slug,
            title,
            thumbnailUrl,
            authorName: author,
            categoryName: category,
            priceInCents,
            mercadopagoPriceInCents,
            currency: course.currency ?? "USD",
            mercadopagoProductId,
            hasFreeChapters: hasFreeChapters ?? false,
          }}
          isEnrolled={isEnrolled}
          isPurchased={isPurchased}
          variant="card-row"
        />
      </div>
    </article>
  );
}
